<script setup lang="ts">
import { onMounted, onUnmounted } from 'vue';

import { usePerformanceStore } from '@apps/main/new-desktop/core/stores';
import {
  HotelDetailsPageComponentAsyncImport,
  TripOverviewPageComponentAsyncImport,
} from '@apps/main/modules/router/tripPlannerRoute';

import { AppInfoBox } from '@libs/ui';
import { performanceMark, useBackgroundComponentsPreload } from '@libs/utils';

import type { HotelSelectionCardData } from '../models';
import { useHotelSelectionListStore } from '../stores/hotelSelection/hotelSelectionListStore';

import HotelSelectionDynamicScroller from './HotelSelectionDynamicScroller.vue';

interface HotelSelectionListProps {
  data: HotelSelectionCardData[];
  shouldDisplayLoader: boolean;
  isMobile?: boolean;
}

const props = defineProps<HotelSelectionListProps>();

interface HotelSelectionListEmits {
  (eventName: 'hightlight-on-map', hotelCode: string | undefined): void;
  (eventName: 'initialized-hotel-selection-list'): void;
}

const emit = defineEmits<HotelSelectionListEmits>();

const hotelSelectionListStore = useHotelSelectionListStore();
const performanceStore = usePerformanceStore();

const preloadComponents = useBackgroundComponentsPreload([
  TripOverviewPageComponentAsyncImport,
  HotelDetailsPageComponentAsyncImport,
]);

onMounted(() => {
  if (hotelSelectionListStore.isFirstHotelSelectionListRender) {
    if (props.data.length) {
      performanceMark('HotelSelectionListRendered');
    }
    hotelSelectionListStore.isFirstHotelSelectionListRender = false;
  }

  if (props.data.length) {
    performanceStore.trackHotelSelectionPageRenderedWithContentPerformance();

    preloadComponents.start();
  }
});

onUnmounted(() => preloadComponents.stop());
</script>

<template>
  <div class="hotel-selection-list">
    <HotelSelectionDynamicScroller
      v-if="props.data.length || props.shouldDisplayLoader"
      :data="props.data"
      :is-mobile="props.isMobile"
      :should-display-loader="props.shouldDisplayLoader"
      @hightlight-on-map="emit('hightlight-on-map', $event)"
      @initialized-hotel-selection-list="
        emit('initialized-hotel-selection-list')
      "
    />
    <AppInfoBox v-else :text="$t('planner.no_accommodation_available')" />
  </div>
</template>

<style scoped lang="scss">
.hotel-selection-list {
  display: flex;
  flex-direction: column;
  height: 100%;
}
</style>
